// src/components/myAccount/pages/EnhancedPaymentMethodsPage.js
import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Plus, 
  Trash2, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  Shield,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import stripeService from '../../../services/stripeService';
import StripeElementsWrapper from '../../ui/StripeElements';

const EnhancedPaymentMethodsPage = ({ user }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [customerId, setCustomerId] = useState(null);

  useEffect(() => {
    if (user?.id) {
      initializeData();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const initializeData = async () => {
    try {
      setLoading(true);
      
      // Get customer ID
      const cId = await stripeService.getCustomerId(user.id);
      setCustomerId(cId);
      
      if (cId) {
        await fetchPaymentMethods(cId);
      } else {
        setPaymentMethods([]);
      }
    } catch (error) {
      console.error('Error initializing data:', error);
      setError('Failed to load payment information');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentMethods = async (cId) => {
    try {
      const response = await stripeService.getPaymentMethods(cId);
      if (response.success) {
        // Get default payment method from customer
        const customer = await fetch(`/api/stripe_api/customer/${cId}`).catch(() => null);
        let defaultPaymentMethodId = null;
        if (customer?.ok) {
          const customerData = await customer.json();
          defaultPaymentMethodId = customerData.customer?.invoice_settings?.default_payment_method;
        }
        
        const methods = response.payment_methods.map(pm => ({
          ...pm,
          is_default: pm.id === defaultPaymentMethodId
        }));
        setPaymentMethods(methods);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      setError('Failed to load payment methods');
    }
  };


  const handleAddPaymentMethod = async (paymentMethodData) => {
    if (!customerId) {
      setError('Customer ID not found. Please contact support.');
      return;
    }

    setLoading(true);
    setMessage('');
    setError('');

    try {
      const isFirstMethod = paymentMethods.length === 0;
      
      // Attach payment method to customer
      await stripeService.addPaymentMethod(customerId, {
        payment_method_id: paymentMethodData.paymentMethodId,
        set_as_default: isFirstMethod
      });

      // Refresh payment methods
      await fetchPaymentMethods(customerId);
      
      setMessage('Payment method added successfully!');
      setShowAddForm(false);
    } catch (err) {
      setError(err.message || 'Failed to add payment method.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePaymentMethod = async (methodId) => {
    if (window.confirm('Are you sure you want to delete this payment method?')) {
      try {
        setLoading(true);
        await stripeService.deletePaymentMethod(methodId);
        
        // Refresh payment methods
        if (customerId) {
          await fetchPaymentMethods(customerId);
        }
        
        setMessage('Payment method deleted successfully!');
      } catch (err) {
        setError(err.message || 'Failed to delete payment method.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSetDefault = async (methodId) => {
    if (!customerId) {
      setError('Customer ID not found. Please contact support.');
      return;
    }

    try {
      setLoading(true);
      await stripeService.setDefaultPaymentMethod(customerId, methodId);
      
      // Refresh payment methods
      await fetchPaymentMethods(customerId);
      
      setMessage('Default payment method updated!');
    } catch (err) {
      setError(err.message || 'Failed to update default payment method.');
    } finally {
      setLoading(false);
    }
  };

  const formatCardNumber = (last4) => {
    return `•••• •••• •••• ${last4}`;
  };

  const formatExpiry = (month, year) => {
    return `${month.toString().padStart(2, '0')}/${year.toString().slice(-2)}`;
  };


  const getCardBrandIcon = (brand) => {
    const brandColors = {
      visa: 'text-blue-600',
      mastercard: 'text-red-600',
      amex: 'text-green-600',
      discover: 'text-orange-600'
    };
    return brandColors[brand] || 'text-gray-600';
  };

  const getCardBrandName = (brand) => {
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  if (loading && paymentMethods.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading payment methods...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Methods</h1>
          <p className="text-gray-600">Manage your billing information and payment methods.</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Payment Method
        </button>
      </div>

      {/* Messages */}
      {message && (
        <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded flex items-center">
          <CheckCircle className="h-4 w-4 mr-2" />
          {message}
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-4 w-4 mr-2" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Payment Methods */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Your Payment Methods</h2>
          
          {paymentMethods.length > 0 ? (
            <div className="space-y-4">
              {paymentMethods.map((method) => (
                <div key={method.id} className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="h-12 w-12 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                        <CreditCard className={`h-6 w-6 ${getCardBrandIcon(method.card.brand)}`} />
                      </div>
                      <div>
                        <div className="flex items-center">
                          <span className="font-medium text-gray-900">
                            {getCardBrandName(method.card.brand)} {formatCardNumber(method.card.last4)}
                          </span>
                          {method.is_default && (
                            <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full ml-2">
                              Default
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600">
                          Expires {formatExpiry(method.card.exp_month, method.card.exp_year)}
                          {method.billing_details?.name && ` • ${method.billing_details.name}`}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {!method.is_default && (
                        <button
                          onClick={() => handleSetDefault(method.id)}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium px-3 py-1 rounded border border-blue-600 hover:bg-blue-50"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        onClick={() => handleDeletePaymentMethod(method.id)}
                        className="text-red-600 hover:text-red-700 p-2 rounded hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Payment Methods</h3>
              <p className="text-gray-600 mb-4">Add a payment method to manage your subscriptions.</p>
              <button
                onClick={() => setShowAddForm(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Add Your First Payment Method
              </button>
            </div>
          )}
        </div>

        {/* Billing History Link */}
        <div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Billing History</h2>
                <p className="text-gray-600">View all your past invoices and transactions</p>
              </div>
              <Link
                to="/my-account/billing-history"
                className="flex items-center text-blue-600 hover:text-blue-700 font-medium"
              >
                View All
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Add Payment Method Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Add Payment Method</h3>
                <button
                  onClick={() => setShowAddForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              {customerId ? (
                <StripeElementsWrapper 
                  onSubmit={handleAddPaymentMethod}
                  loading={loading}
                  user={user}
                />
              ) : (
                <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                  <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600 text-sm">
                    Unable to add payment method. Please ensure you have an active subscription.
                  </p>
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Security Notice */}
      <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start">
          <Shield className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">Secure Payment Processing</p>
            <p>
              Your payment information is encrypted and securely processed through Stripe. 
              We never store your complete card details on our servers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedPaymentMethodsPage;
